/**
 * Carousel Component
 * Based on shadcn/ui + Figma design specification
 * @description shadcn/ui 기반 컴포넌트. CSS 변수를 활용한 테마 시스템 적용.
 */

import { createContext, useContext, useState, useEffect, Children, isValidElement, ReactNode } from 'react';
import { cn } from '../lib/utils';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from 'lucide-react';

export type CarouselOrientation = 'horizontal' | 'vertical';

export interface CarouselProps {
  children: ReactNode;
  orientation?: CarouselOrientation;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  showDots?: boolean;
  showArrows?: boolean;
  className?: string;
}

export interface CarouselItemProps {
  children: ReactNode;
  className?: string;
}

export interface CarouselContextValue {
  currentIndex: number;
  totalItems: number;
  goTo: (index: number) => void;
  goNext: () => void;
  goPrev: () => void;
  orientation: CarouselOrientation;
}

const CarouselContext = createContext<CarouselContextValue | null>(null);

export function Carousel({
  children,
  orientation = 'horizontal',
  autoPlay = false,
  autoPlayInterval = 5000,
  showDots = true,
  showArrows = true,
  className,
}: CarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const items = Children.toArray(children).filter(isValidElement);
  const totalItems = items.length;

  const goTo = (index: number) => {
    setCurrentIndex((index + totalItems) % totalItems);
  };

  const goNext = () => goTo(currentIndex + 1);
  const goPrev = () => goTo(currentIndex - 1);

  useEffect(() => {
    if (!autoPlay) return;
    const interval = setInterval(goNext, autoPlayInterval);
    return () => clearInterval(interval);
  }, [autoPlay, autoPlayInterval, currentIndex]);

  const PrevIcon = orientation === 'horizontal' ? ChevronLeft : ChevronUp;
  const NextIcon = orientation === 'horizontal' ? ChevronRight : ChevronDown;

  return (
    <CarouselContext.Provider value={{ currentIndex, totalItems, goTo, goNext, goPrev, orientation }}>
      <div className={cn('relative overflow-hidden', className)}>
        {/* Slides */}
        <div
          className={cn(
            'flex transition-transform duration-300 ease-out',
            orientation === 'vertical' && 'flex-col'
          )}
          style={{
            transform: orientation === 'horizontal'
              ? `translateX(-${currentIndex * 100}%)`
              : `translateY(-${currentIndex * 100}%)`,
          }}
        >
          {items.map((item, index) => (
            <div
              key={index}
              className={cn(
                'flex-shrink-0',
                orientation === 'horizontal' ? 'w-full' : 'h-full'
              )}
            >
              {item}
            </div>
          ))}
        </div>

        {/* Arrows */}
        {showArrows && totalItems > 1 && (
          <>
            <button
              onClick={goPrev}
              className={cn(
                'absolute z-10 p-2 rounded-full bg-card/80 shadow hover:bg-card transition-colors',
                orientation === 'horizontal'
                  ? 'left-2 top-1/2 -translate-y-1/2'
                  : 'top-2 left-1/2 -translate-x-1/2'
              )}
              aria-label="Previous"
            >
              <PrevIcon className="w-5 h-5 text-foreground" />
            </button>
            <button
              onClick={goNext}
              className={cn(
                'absolute z-10 p-2 rounded-full bg-card/80 shadow hover:bg-card transition-colors',
                orientation === 'horizontal'
                  ? 'right-2 top-1/2 -translate-y-1/2'
                  : 'bottom-2 left-1/2 -translate-x-1/2'
              )}
              aria-label="Next"
            >
              <NextIcon className="w-5 h-5 text-foreground" />
            </button>
          </>
        )}

        {/* Dots */}
        {showDots && totalItems > 1 && (
          <div
            className={cn(
              'absolute z-10 flex gap-1.5',
              orientation === 'horizontal'
                ? 'bottom-3 left-1/2 -translate-x-1/2'
                : 'right-3 top-1/2 -translate-y-1/2 flex-col'
            )}
          >
            {Array.from({ length: totalItems }).map((_, index) => (
              <button
                key={index}
                onClick={() => goTo(index)}
                className={cn(
                  'w-2 h-2 rounded-full transition-colors',
                  currentIndex === index ? 'bg-card' : 'bg-card/50'
                )}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </CarouselContext.Provider>
  );
}

export function CarouselItem({ children, className }: CarouselItemProps) {
  return (
    <div className={cn('w-full h-full', className)}>
      {children}
    </div>
  );
}

export function useCarousel() {
  const context = useContext(CarouselContext);
  if (!context) throw new Error('useCarousel must be used within Carousel');
  return context;
}
